import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import type { DuelPitch } from './videoDuelData';
import { pitchLabel } from '@/utils/pitches';

export type DuelLetter = 'A' | 'B' | 'C';

export interface PitchPositionCell {
  /** Pitch do vídeo nessa posição; null = o vídeo não tem pitch nessa posição. */
  pitch: DuelPitch | null;
  /** Nome exibido (nome salvo ou "Pitch N"). */
  label: string | null;
  /** Retenção no pitch; null = sem pitch ou sem dado. */
  value: number | null;
}

export interface PitchPositionRow {
  cells: PitchPositionCell[];
  winner: DuelLetter | 'tie';
  differenceLabel: string;
}

const LETTERS: DuelLetter[] = ['A', 'B', 'C'];

/**
 * Linha "Retenção no Pitch" do duelo para a posição escolhida (0 = 1º pitch).
 * Vencedor e diferença seguem a regra do backend (`_buildMetricRow`), mas só
 * entre os vídeos que têm pitch nessa posição; com menos de 2, não há disputa.
 */
export function buildPitchPositionRow(
  pitchLists: ReadonlyArray<DuelPitch[] | null | undefined>,
  position: number,
): PitchPositionRow {
  const cells: PitchPositionCell[] = pitchLists.map((list) => {
    const pitch = list?.[position] ?? null;
    return {
      pitch,
      label: pitch ? pitchLabel(pitch, position) : null,
      value: pitch?.retention ?? null,
    };
  });

  const present = cells.flatMap((cell, index) =>
    cell.value != null ? [{ value: cell.value, letter: LETTERS[index] }] : [],
  );
  if (present.length < 2) return { cells, winner: 'tie', differenceLabel: '—' };

  const values = present.map((item) => item.value);
  const max = Math.max(...values);
  if (values.every((value) => value === max)) return { cells, winner: 'tie', differenceLabel: 'Empate' };

  const sorted = [...values].sort((a, b) => b - a);
  const advantage = Math.round((sorted[0] - sorted[1]) * 10) / 10;
  const winner = present.find((item) => item.value === max)?.letter ?? 'tie';
  return {
    cells,
    winner,
    differenceLabel: `+${advantage.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`,
  };
}
